import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Upload } from 'lucide-react';
import { readPortableImport } from '@stageplottter/core';
import { importBoardFile } from '../../api/importBoard';
import { errorMessageKey } from '../../pages/ErrorBox';
import { toastStore } from '../../state/toasts';
import {
  localImportErrorKey,
  percent,
  serverImportErrorKey,
  summarizeBoardFile,
} from './model/boardFile';
import type { BoardFileSummary } from './model/boardFile';
import { acceptsBoardFile } from './model/files';

type Phase =
  | { kind: 'idle' }
  | { kind: 'reading'; name: string }
  | { kind: 'uploading'; summary: BoardFileSummary; pct: number }
  | { kind: 'error'; message: string };

/**
 * "Import board…" on the boards page (decision U7): file picker or drag & drop of a
 * `.stageplottter` file (or an old v1 `.json` export) anywhere on the page → new board.
 */
export function BoardImport({
  workspaceId,
  children,
}: {
  workspaceId: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [dragging, setDragging] = useState(false);
  const busy = phase.kind === 'reading' || phase.kind === 'uploading';

  const run = async (file: File) => {
    if (busy) return;
    if (!acceptsBoardFile(file)) {
      setPhase({ kind: 'error', message: t('boardImport.error.wrong-type', { name: file.name }) });
      return;
    }
    setPhase({ kind: 'reading', name: file.name });
    const local = readPortableImport(await file.text());
    if (!local.ok) {
      setPhase({
        kind: 'error',
        message: t(localImportErrorKey(local.error), { message: local.message }),
      });
      return;
    }
    const summary = summarizeBoardFile(local.data, Object.keys(local.files).length);
    setPhase({ kind: 'uploading', summary, pct: 0 });
    try {
      const board = await importBoardFile(workspaceId, file, {
        onProgress: (loaded, total) =>
          setPhase({ kind: 'uploading', summary, pct: percent(loaded, total) }),
      });
      toastStore
        .getState()
        .push({ kind: 'success', message: t('boardImport.done', { name: board.name }) });
      navigate(`/b/${board.id}`);
    } catch (err) {
      const key = serverImportErrorKey(err) ?? errorMessageKey(err);
      setPhase({ kind: 'error', message: t(key, { message: (err as Error).message }) });
    }
  };

  // Drag & drop anywhere on the page.
  useEffect(() => {
    const hasFiles = (e: DragEvent) => e.dataTransfer?.types.includes('Files') ?? false;
    const over = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setDragging(true);
    };
    const leave = (e: DragEvent) => {
      if (e.relatedTarget === null) setDragging(false);
    };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setDragging(false);
      const file = e.dataTransfer?.files[0];
      if (file) void run(file);
    };
    window.addEventListener('dragover', over);
    window.addEventListener('dragleave', leave);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragover', over);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('drop', drop);
    };
  });

  return (
    <div className={`board-import${dragging ? ' board-import-drop' : ''}`}>
      <div className="button-row">
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          <Upload size={15} aria-hidden="true" /> {t('boardImport.button')}
        </button>
        {children}
        <input
          ref={inputRef}
          type="file"
          accept=".stageplottter,.json,application/json"
          className="sr-only"
          tabIndex={-1}
          aria-label={t('boardImport.button')}
          data-testid="board-import-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void run(f);
          }}
        />
      </div>
      <p className="muted small">{dragging ? t('boardImport.dropHere') : t('boardImport.hint')}</p>
      <div aria-live="polite">
        {phase.kind === 'reading' && (
          <p className="muted">{t('boardImport.reading', { name: phase.name })}</p>
        )}
        {phase.kind === 'uploading' && (
          <div className="board-import-progress">
            <p className="muted">
              {t('boardImport.uploading', {
                name: phase.summary.name,
                items: phase.summary.items,
                channels: phase.summary.channels,
                files: phase.summary.files,
              })}
            </p>
            <progress max={100} value={phase.pct} aria-label={t('boardImport.progress')}>
              {phase.pct} %
            </progress>
          </div>
        )}
      </div>
      {phase.kind === 'error' && (
        <p role="alert" className="error-text">
          {phase.message}
        </p>
      )}
    </div>
  );
}
